import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adApi } from '../../../api/ad.api';
import {
  RewardBreakdownResponse,
  RewardConfigItem,
  RewardBreakdownDaily,
} from '../../../types/Ad.api.type';
import { Card } from '../../ui/card';
import { Badge } from '../../ui/badge';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  Cell,
  PieChart,
  Pie,
} from 'recharts';

/** 타입별 색상 팔레트 */
const TYPE_COLORS: Record<string, string> = {
  VOTE: '#3b82f6',
  VOTE_AD_BONUS: '#60a5fa',
  DAILY_BONUS: '#8b5cf6',
  DAILY_AD_BONUS: '#a78bfa',
  WEEKLY_MILESTONE_1: '#10b981',
  WEEKLY_MILESTONE_2: '#34d399',
  WEEKLY_MILESTONE_3: '#6ee7b7',
  WEEKLY_MILESTONE_4: '#a7f3d0',
  CREATOR_MILESTONE: '#f59e0b',
  MILESTONE_AD_BONUS: '#fbbf24',
  FIRST_VOTE_BONUS: '#ef4444',
  ATTENDANCE: '#06b6d4',
  ATTENDANCE_AD_BONUS: '#22d3ee',
  ATTENDANCE_STREAK: '#0891b2',
  ATTENDANCE_STREAK_SAVE: '#67e8f9',
  COMMENT: '#ec4899',
  COMMENT_AD_BONUS: '#f472b6',
  SURVEY_LIKE: '#f97316',
  SURVEY_LIKE_AD_BONUS: '#fb923c',
  COMMENT_LIKE: '#d946ef',
  COMMENT_LIKE_AD_BONUS: '#e879f9',
  WEEKLY_MISSION: '#14b8a6',
  WEEKLY_MISSION_AD_BONUS: '#2dd4bf',
};

const getColor = (type: string) => TYPE_COLORS[type] || '#9ca3af';

const fmt = (n: number) => n.toLocaleString('ko-KR');

interface RewardBreakdownSectionProps {
  from: string;
  to: string;
}

export const RewardBreakdownSection = ({ from, to }: RewardBreakdownSectionProps) => {
  const [viewMode, setViewMode] = useState<'chart' | 'table'>('chart');

  const { data, isLoading } = useQuery({
    queryKey: ['rewardBreakdown', from, to],
    queryFn: () => adApi.getRewardBreakdown(from, to).then(res => res.data as RewardBreakdownResponse),
    meta: { hideGlobalLoader: true },
  });

  if (isLoading || !data) {
    return <div className="text-center py-8 text-gray-400">리워드 체계 분석 로딩 중...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-bold text-gray-900">리워드 체계 분석</h4>
        <div className="flex gap-1">
          <button
            className={`px-2 py-1 text-xs rounded ${viewMode === 'chart' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600'}`}
            onClick={() => setViewMode('chart')}
          >
            차트
          </button>
          <button
            className={`px-2 py-1 text-xs rounded ${viewMode === 'table' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600'}`}
            onClick={() => setViewMode('table')}
          >
            표
          </button>
        </div>
      </div>

      {/* 현재 설정 테이블 */}
      <ConfigTable configs={data.configs} />

      {/* 타입별 일별 차트 or 테이블 */}
      {viewMode === 'chart' ? (
        <DailyStackedChart daily={data.daily} />
      ) : (
        <DailyBreakdownTable daily={data.daily} />
      )}

      {/* 기간 합계 비중 */}
      <PeriodSummary daily={data.daily} />
    </div>
  );
};

// ==================== 설정 테이블 ====================

const ConfigTable = ({ configs }: { configs: RewardConfigItem[] }) => (
  <Card>
    <div className="p-4">
      <p className="text-xs font-semibold text-gray-700 mb-3">현재 리워드 설정</p>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b text-gray-500">
              <th className="text-left py-1.5 pr-3">타입</th>
              <th className="text-right py-1.5 px-2">확률</th>
              <th className="text-right py-1.5 px-2">지급량</th>
              <th className="text-center py-1.5 px-2">고정</th>
              <th className="text-center py-1.5 px-2">상태</th>
            </tr>
          </thead>
          <tbody>
            {configs.map(c => (
              <tr key={c.rewardType} className="border-b border-gray-50 hover:bg-gray-50">
                <td className="py-1.5 pr-3">
                  <span className="inline-block w-2 h-2 rounded-full mr-1.5" style={{ backgroundColor: getColor(c.rewardType) }} />
                  {c.label}
                </td>
                <td className="text-right py-1.5 px-2 font-mono">{c.probability}%</td>
                <td className="text-right py-1.5 px-2 font-mono">
                  {c.amounts.length === 1 ? `${c.amounts[0]}` : `${c.amounts[0]}~${c.amounts[c.amounts.length - 1]}`}
                </td>
                <td className="text-center py-1.5 px-2">
                  {c.isFixed ? <Badge variant="outline" className="text-[10px] px-1">고정</Badge> : '-'}
                </td>
                <td className="text-center py-1.5 px-2">
                  <Badge variant={c.enabled ? 'default' : 'secondary'} className="text-[10px] px-1">
                    {c.enabled ? '활성' : '비활성'}
                  </Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </Card>
);

// ==================== 스택 바 차트 ====================

const DailyStackedChart = ({ daily }: { daily: RewardBreakdownDaily[] }) => {
  // 활성 타입 추출 (기간 내 데이터 있는 타입만)
  const activeTypes = new Set<string>();
  daily.forEach(d => d.types.forEach(t => activeTypes.add(t.rewardType)));
  const typeList = Array.from(activeTypes);

  // recharts 데이터 변환: {date, VOTE: 100, DAILY_BONUS: 50, ...}
  const chartData = daily.map(d => {
    const row: Record<string, string | number> = { date: d.date.slice(5) }; // MM-DD
    d.types.forEach(t => { row[t.rewardType] = t.pieces; });
    return row;
  });

  // 라벨 매핑
  const labelMap: Record<string, string> = {};
  daily.forEach(d => d.types.forEach(t => { labelMap[t.rewardType] = t.label; }));

  return (
    <Card>
      <div className="p-4">
        <p className="text-xs font-semibold text-gray-700 mb-3">타입별 일일 조각 발행</p>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={chartData} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="date" tick={{ fontSize: 10 }} />
            <YAxis tick={{ fontSize: 10 }} />
            <Tooltip
              contentStyle={{ fontSize: 11 }}
              formatter={(value, name) => [fmt(Number(value ?? 0)), labelMap[String(name)] || String(name)]}
            />
            <Legend
              wrapperStyle={{ fontSize: 10 }}
              formatter={(value: string) => labelMap[value] || value}
            />
            {typeList.map(type => (
              <Bar key={type} dataKey={type} stackId="pieces" fill={getColor(type)} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
};

// ==================== 일별 테이블 ====================

const DailyBreakdownTable = ({ daily }: { daily: RewardBreakdownDaily[] }) => {
  // 활성 타입 추출
  const activeTypes = new Map<string, string>();
  daily.forEach(d => d.types.forEach(t => activeTypes.set(t.rewardType, t.label)));
  const typeList = Array.from(activeTypes.entries());

  return (
    <Card>
      <div className="p-4">
        <p className="text-xs font-semibold text-gray-700 mb-3">타입별 일일 조각 발행 (상세)</p>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b text-gray-500">
                <th className="text-left py-1.5 pr-2 sticky left-0 bg-white">날짜</th>
                {typeList.map(([type, label]) => (
                  <th key={type} className="text-right py-1.5 px-1.5 whitespace-nowrap">{label}</th>
                ))}
                <th className="text-right py-1.5 px-1.5 font-bold">합계</th>
              </tr>
            </thead>
            <tbody>
              {daily.map(d => {
                const typeMap = new Map(d.types.map(t => [t.rewardType, t]));
                return (
                  <tr key={d.date} className="border-b border-gray-50 hover:bg-gray-50">
                    <td className="py-1 pr-2 font-mono sticky left-0 bg-white">{d.date.slice(5)}</td>
                    {typeList.map(([type]) => {
                      const t = typeMap.get(type);
                      return (
                        <td key={type} className="text-right py-1 px-1.5 font-mono text-gray-600">
                          {t ? fmt(t.pieces) : '-'}
                        </td>
                      );
                    })}
                    <td className="text-right py-1 px-1.5 font-mono font-bold">{fmt(d.totalPieces)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </Card>
  );
};

// ==================== 기간 합계 비중 ====================

const PeriodSummary = ({ daily }: { daily: RewardBreakdownDaily[] }) => {
  // 기간 합산
  const totals = new Map<string, { label: string; count: number; pieces: number }>();
  daily.forEach(d =>
    d.types.forEach(t => {
      const existing = totals.get(t.rewardType) || { label: t.label, count: 0, pieces: 0 };
      existing.count += t.count;
      existing.pieces += t.pieces;
      totals.set(t.rewardType, existing);
    })
  );

  const sorted = Array.from(totals.entries())
    .map(([type, v]) => ({ type, ...v }))
    .sort((a, b) => b.pieces - a.pieces);

  const grandTotal = sorted.reduce((sum, s) => sum + s.pieces, 0);

  // 파이 차트 데이터
  const pieData = sorted.map(s => ({
    name: s.label,
    value: s.pieces,
    fill: getColor(s.type),
  }));

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {/* 비중 테이블 */}
      <Card>
        <div className="p-4">
          <p className="text-xs font-semibold text-gray-700 mb-3">기간 합계 비중</p>
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b text-gray-500">
                <th className="text-left py-1.5">타입</th>
                <th className="text-right py-1.5 px-2">건수</th>
                <th className="text-right py-1.5 px-2">조각</th>
                <th className="text-right py-1.5 px-2">비중</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map(s => (
                <tr key={s.type} className="border-b border-gray-50">
                  <td className="py-1">
                    <span className="inline-block w-2 h-2 rounded-full mr-1.5" style={{ backgroundColor: getColor(s.type) }} />
                    {s.label}
                  </td>
                  <td className="text-right py-1 px-2 font-mono">{fmt(s.count)}</td>
                  <td className="text-right py-1 px-2 font-mono">{fmt(s.pieces)}</td>
                  <td className="text-right py-1 px-2 font-mono">
                    {grandTotal > 0 ? ((s.pieces / grandTotal) * 100).toFixed(1) : '0.0'}%
                  </td>
                </tr>
              ))}
              <tr className="border-t font-bold">
                <td className="py-1">합계</td>
                <td className="text-right py-1 px-2 font-mono">{fmt(sorted.reduce((s, r) => s + r.count, 0))}</td>
                <td className="text-right py-1 px-2 font-mono">{fmt(grandTotal)}</td>
                <td className="text-right py-1 px-2 font-mono">100%</td>
              </tr>
            </tbody>
          </table>
        </div>
      </Card>

      {/* 파이 차트 */}
      <Card>
        <div className="p-4">
          <p className="text-xs font-semibold text-gray-700 mb-3">조각 발행 비중</p>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={pieData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={90}
                innerRadius={40}
                paddingAngle={1}
                label={({ name, percent }) => `${name} ${((percent ?? 0) * 100).toFixed(0)}%`}
                labelLine={false}
                style={{ fontSize: 9 }}
              >
                {pieData.map((entry, i) => (
                  <Cell key={i} fill={entry.fill} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{ fontSize: 11 }}
                formatter={(value) => [fmt(Number(value ?? 0)), '조각']}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </Card>
    </div>
  );
};
